import React from 'react'

import { Chip, Theme, makeStyles } from '@material-ui/core'
import BuildIcon from '@material-ui/icons/Build'

import { ReactComponent as FileSBIcon } from '../../assets/icons/file.svg'
import { useTimezone } from '../../contexts/timezone'
import { RecordsTimelineEvent } from '../../hooks/useRecords'
import {
    getServiceImageDetails,
    getUiStatus,
    WorkorderStatus,
} from '../../models'
import { kindLabel } from './trailKinds'

const KIND_STYLES: Record<string, { color: string; bg: string }> = {
    lease: { color: '#1565c0', bg: 'rgba(21, 101, 192, 0.12)' },
    service_request: { color: '#e65100', bg: 'rgba(230, 81, 0, 0.12)' },
    staff_request: { color: '#e65100', bg: 'rgba(230, 81, 0, 0.12)' },
    message: { color: '#00695c', bg: 'rgba(0, 105, 92, 0.12)' },
    call: { color: '#00695c', bg: 'rgba(0, 105, 92, 0.12)' },
    note: { color: '#5d4037', bg: 'rgba(93, 64, 55, 0.12)' },
    inspection: { color: '#283593', bg: 'rgba(40, 53, 147, 0.12)' },
    move_in_inspection: { color: '#283593', bg: 'rgba(40, 53, 147, 0.12)' },
    workorder: { color: '#546e7a', bg: 'rgba(84, 110, 122, 0.14)' },
    damage: { color: '#c62828', bg: 'rgba(198, 40, 40, 0.12)' },
    incident: { color: '#ef6c00', bg: 'rgba(239, 108, 0, 0.12)' },
    pet: { color: '#2e7d32', bg: 'rgba(46, 125, 50, 0.12)' },
    offer: { color: '#6a1b9a', bg: 'rgba(106, 27, 154, 0.12)' },
    signing: { color: '#6a1b9a', bg: 'rgba(106, 27, 154, 0.12)' },
}

const LEASE_STATUS_STYLES: Record<string, { color: string; bg: string }> = {
    Current: { color: '#2e7d32', bg: 'rgba(46, 125, 50, 0.12)' },
    Notice: { color: '#f9a825', bg: 'rgba(249, 168, 37, 0.16)' },
    Future: { color: '#1565c0', bg: 'rgba(21, 101, 192, 0.12)' },
    Pending: { color: '#6a1b9a', bg: 'rgba(106, 27, 154, 0.12)' },
    Applicant: { color: '#6a1b9a', bg: 'rgba(106, 27, 154, 0.12)' },
    Past: { color: '#546e7a', bg: 'rgba(84, 110, 122, 0.14)' },
    Cancelled: { color: '#c62828', bg: 'rgba(198, 40, 40, 0.12)' },
}

const WORKORDER_STATUS_COLORS: Record<string, string> = {
    STAGED: '#9e9e9e',
    REJECTED: '#e73535',
    ASSIGNED: '#9e9e9e',
    IN_PROGRESS: '#ffbf00',
    PAUSE: '#ed6c02',
    COMPLETE: '#46b083',
    GO_BACK: '#ff7459',
    PRE_APPROVED: '#016fb9',
    APPROVED: '#016fb9',
    INVOICE_REQUEST: '#00acc1',
    INVOICED: '#00acc1',
    INVOICED_OUT: '#00acc1',
}

const hexToRgb = (hex: string) => {
    const raw = hex.replace('#', '')
    if (raw.length !== 6) return null
    const n = parseInt(raw, 16)
    return {
        r: (n >> 16) & 255,
        g: (n >> 8) & 255,
        b: n & 255,
    }
}

const hexToRgba = (hex: string, alpha: number) => {
    const rgb = hexToRgb(hex)
    if (!rgb) return `rgba(84, 110, 122, ${alpha})`
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

const luminance = (hex: string) => {
    const rgb = hexToRgb(hex)
    if (!rgb) return 0
    const channel = (value: number) => {
        const s = value / 255
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
    }
    return (
        0.2126 * channel(rgb.r) +
        0.7152 * channel(rgb.g) +
        0.0722 * channel(rgb.b)
    )
}

const darkenHex = (hex: string, amount = 0.52) => {
    const rgb = hexToRgb(hex)
    if (!rgb) return hex
    const toHex = (value: number) =>
        Math.round(value * amount)
            .toString(16)
            .padStart(2, '0')
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`
}

const readableTextColor = (hex: string) =>
    luminance(hex) > 0.4 ? darkenHex(hex) : hex

const humanize = (value: string) =>
    value
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')

const kindStyle = (kind: string) =>
    KIND_STYLES[kind] || { color: '#546e7a', bg: 'rgba(84, 110, 122, 0.14)' }

const leaseStatusStyle = (status: string) =>
    LEASE_STATUS_STYLES[status] || {
        color: '#1565c0',
        bg: 'rgba(21, 101, 192, 0.12)',
    }

const workorderStatusStyle = (status: string) => {
    const color = WORKORDER_STATUS_COLORS[status] || '#546e7a'
    const ui = getUiStatus(status as WorkorderStatus)
    return {
        label: ui === '?' ? humanize(status) : ui,
        color: readableTextColor(color),
        bg: hexToRgba(color, 0.14),
    }
}

const useStyles = makeStyles((theme: Theme) => ({
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },
    card: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        padding: theme.spacing(1.5, 1.75),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        borderLeftWidth: 4,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition:
            'border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
    },
    cardButton: {
        cursor: 'pointer',
        '&:hover': {
            borderColor: theme.palette.primary.light,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            backgroundColor: 'rgba(25, 118, 210, 0.03)',
        },
    },
    iconWrap: {
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    serviceIcon: {
        maxHeight: 22,
        maxWidth: 24,
    },
    leaseIcon: {
        width: 22,
        height: 22,
        display: 'block',
        '& path': {
            stroke: 'currentColor',
        },
    },
    cardSubtitle: {
        fontWeight: 600,
        fontSize: '0.85rem',
        lineHeight: 1.35,
        color: theme.palette.text.primary,
    },
    facts: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        padding: theme.spacing(1, 1.25),
        borderRadius: 8,
        backgroundColor: theme.palette.grey[50],
    },
    fact: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 72,
        gap: 2,
    },
    factLabel: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    factValue: {
        fontSize: '0.8rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    associated: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        alignItems: 'center',
    },
    associatedLabel: {
        fontSize: '0.7rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    cardBody: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    cardTitle: {
        fontWeight: 600,
        fontSize: '0.95rem',
        lineHeight: 1.35,
        color: theme.palette.text.primary,
    },
    cardChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        alignItems: 'center',
    },
    metaChip: {
        height: 22,
        fontWeight: 600,
        fontSize: '0.7rem',
    },
    cardMeta: {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
        lineHeight: 1.4,
    },
}))

interface TrailEventCardProps {
    event: RecordsTimelineEvent
    onClick?: () => void
}

export const TrailEventList = ({ children }: { children: React.ReactNode }) => {
    const classes = useStyles()
    return <div className={classes.cardList}>{children}</div>
}

export const TrailEventCard = ({ event, onClick }: TrailEventCardProps) => {
    const classes = useStyles()
    const { formatDate } = useTimezone()
    const dateOnly = (value?: string | null) =>
        value ? formatDate(value, 'date-only') : ''
    const isWork = event.kind === 'workorder'
    const isLease = event.kind === 'lease'
    const kind = kindStyle(event.kind)
    const serviceVisual = getServiceImageDetails(event.service_id ?? -1)
    const accent = event.service_color || serviceVisual.color || kind.color
    const accentText = readableTextColor(accent)
    const iconBg = hexToRgba(accent, 0.14)
    const dateLabel = dateOnly(event.at)
    const leaseStatus = event.status ? leaseStatusStyle(event.status) : null
    const status = event.status
        ? isWork
            ? workorderStatusStyle(event.status)
            : isLease
            ? {
                  label: event.status,
                  color: leaseStatus?.color || kind.color,
                  bg: leaseStatus?.bg || kind.bg,
              }
            : {
                  label: humanize(event.status),
                  color: kind.color,
                  bg: kind.bg,
              }
        : null
    const associated = event.associated_areas ?? []
    const startLabel = dateOnly(event.start_date) || dateLabel
    const endLabel = dateOnly(event.end_date)
    const metaLabel = isLease
        ? ''
        : [dateLabel, event.detail].filter(Boolean).join(' · ')

    const icon =
        isWork && serviceVisual.icon ? (
            <img
                src={serviceVisual.icon}
                alt={event.service_name || 'Service'}
                className={classes.serviceIcon}
            />
        ) : isLease ? (
            <FileSBIcon className={classes.leaseIcon} />
        ) : (
            <BuildIcon style={{ fontSize: 22 }} />
        )

    const body = (
        <>
            <div
                className={classes.iconWrap}
                style={{
                    backgroundColor: iconBg,
                    color: accentText,
                }}
            >
                {icon}
            </div>
            <div className={classes.cardBody}>
                <div className={classes.cardTitle}>
                    {event.title || kindLabel(event.kind)}
                </div>
                {isLease && event.area_label ? (
                    <div className={classes.cardSubtitle}>
                        {event.area_label}
                    </div>
                ) : null}
                <div className={classes.cardChips}>
                    {isWork && event.service_name ? (
                        <Chip
                            size="small"
                            label={event.service_name}
                            className={classes.metaChip}
                            style={{
                                backgroundColor: iconBg,
                                color: accentText,
                            }}
                        />
                    ) : isLease ? null : (
                        <Chip
                            size="small"
                            label={kindLabel(event.kind)}
                            className={classes.metaChip}
                            style={{
                                backgroundColor: kind.bg,
                                color: kind.color,
                            }}
                        />
                    )}
                    {status ? (
                        <Chip
                            size="small"
                            label={status.label}
                            className={classes.metaChip}
                            style={{
                                backgroundColor: status.bg,
                                color: status.color,
                            }}
                        />
                    ) : null}
                    {isWork && event.vendor ? (
                        <Chip
                            size="small"
                            label={event.vendor}
                            className={classes.metaChip}
                        />
                    ) : null}
                </div>
                {isLease ? (
                    <div className={classes.facts}>
                        <div className={classes.fact}>
                            <span className={classes.factLabel}>Start</span>
                            <span className={classes.factValue}>
                                {startLabel || '—'}
                            </span>
                        </div>
                        <div className={classes.fact}>
                            <span className={classes.factLabel}>End</span>
                            <span className={classes.factValue}>
                                {endLabel || '—'}
                            </span>
                        </div>
                        {event.unit_name ? (
                            <div className={classes.fact}>
                                <span className={classes.factLabel}>Unit</span>
                                <span className={classes.factValue}>
                                    {event.unit_name}
                                </span>
                            </div>
                        ) : null}
                    </div>
                ) : null}
                {isLease && associated.length > 0 ? (
                    <div className={classes.associated}>
                        <span className={classes.associatedLabel}>
                            Also includes
                        </span>
                        {associated.map((area) => (
                            <Chip
                                key={area}
                                size="small"
                                label={area}
                                className={classes.metaChip}
                            />
                        ))}
                    </div>
                ) : null}
                {metaLabel ? (
                    <div className={classes.cardMeta}>{metaLabel}</div>
                ) : null}
            </div>
        </>
    )

    const cardStyle = { borderLeftColor: accent }
    if (onClick) {
        return (
            <button
                type="button"
                className={`${classes.card} ${classes.cardButton}`}
                style={cardStyle}
                onClick={onClick}
            >
                {body}
            </button>
        )
    }
    return (
        <div className={classes.card} style={cardStyle}>
            {body}
        </div>
    )
}
